import DOMPurify from 'isomorphic-dompurify'

export default ({ app }, inject) => {
    /**
     * Sanitize HTML to prevent XSS attacks
     * @param {string} dirty - The potentially unsafe HTML string
     * @param {object} config - Optional DOMPurify configuration
     * @returns {string} - Sanitized HTML string
     */
    const sanitizeHtml = (dirty, config = {}) => {
        if (!dirty || typeof dirty !== 'string') {
            return ''
        }

        // Default configuration - strict but allows common HTML
        const defaultConfig = {
            ALLOWED_TAGS: [
                'p', 'br', 'strong', 'em', 'u', 'a', 'ul', 'ol', 'li',
                'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
                'blockquote', 'code', 'pre',
                'div', 'span', 'img'
            ],
            ALLOWED_ATTR: [
                'href', 'title', 'target', 'rel',
                'src', 'alt', 'width', 'height',
                'class', 'id'
            ],
            ALLOW_DATA_ATTR: false,
            ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|cid|xmpp):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
        }

        // Merge with custom config
        const finalConfig = { ...defaultConfig, ...config }

        return DOMPurify.sanitize(dirty, finalConfig)
    }

    /**
     * Sanitize HTML with very strict settings (text only, no HTML tags)
     * @param {string} dirty - The potentially unsafe HTML string
     * @returns {string} - Plain text string
     */
    const sanitizeText = (dirty) => {
        if (!dirty || typeof dirty !== 'string') {
            return ''
        }

        return DOMPurify.sanitize(dirty, {
            ALLOWED_TAGS: [],
            ALLOWED_ATTR: [],
            KEEP_CONTENT: true
        })
    }

    /**
     * Sanitize HTML for user-generated content with custom HTML
     * Use with EXTREME caution - only for trusted admin users
     * @param {string} dirty - The potentially unsafe HTML string
     * @returns {string} - Sanitized HTML string
     */
    const sanitizeCustomHtml = (dirty) => {
        if (!dirty || typeof dirty !== 'string') {
            return ''
        }

        return DOMPurify.sanitize(dirty, {
            ALLOWED_TAGS: [
                'p', 'br', 'strong', 'em', 'u', 'a', 'ul', 'ol', 'li',
                'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
                'blockquote', 'code', 'pre',
                'div', 'span', 'img', 'iframe',
                'table', 'thead', 'tbody', 'tr', 'th', 'td',
                'label', 'input', 'button'
            ],
            ALLOWED_ATTR: [
                'href', 'title', 'target', 'rel',
                'src', 'alt', 'width', 'height',
                'class', 'id', 'style',
                'frameborder', 'allowfullscreen',
                'type', 'value', 'placeholder'
            ],
            ALLOW_DATA_ATTR: false,
            ADD_TAGS: ['iframe'],
            ADD_ATTR: ['frameborder', 'allowfullscreen'],
        })
    }

    // Inject into Vue instance
    inject('sanitizeHtml', sanitizeHtml)
    inject('sanitizeText', sanitizeText)
    inject('sanitizeCustomHtml', sanitizeCustomHtml)
}
